import { Badge, Table, TableBody, TableCell, TableHeader, TableRow } from '@catering/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, type JSX, useState } from 'react';

import type { ProductionLine } from '../../../production/api';
import { resolveCatalogName, type CatalogNameMap } from '../../../production/use-catalog-names';

const CRITICAL_DIETARY_TAGS = new Set(['SIN_TACC', 'VEGANO']);

export interface ProductionLineTableProps {
  lines: ProductionLine[];
  /** `false` for print: every breakdown row shows at once, nothing to click on paper. */
  expandable?: boolean;
  /** `code -> name` maps from the catalog; a code with no entry falls back to itself. */
  serviceTypeNames?: CatalogNameMap;
  dietaryTagNames?: CatalogNameMap;
}

/**
 * `production-sheets/spec.md` → "Desglose por sede refleja el origen de
 * cada porción": one row per menu, with an expandable row underneath for
 * `byCustomerSite`. Shared by `ProductionSheetViewPage`'s three tabs and
 * `ProductionSheetPrintPage` (`expandable={false}`, always-open rows).
 */
export function ProductionLineTable({
  lines,
  expandable = true,
  serviceTypeNames = {},
  dietaryTagNames = {},
}: ProductionLineTableProps): JSX.Element {
  const [expandedMenuId, setExpandedMenuId] = useState<string | null>(null);

  return (
    <Table>
      <caption className="sr-only">Consolidado de producción por menú</caption>
      <thead>
        <tr>
          <TableHeader>Menú</TableHeader>
          <TableHeader>Tipo de servicio</TableHeader>
          <TableHeader>Etiquetas dietarias</TableHeader>
          <TableHeader>Cantidad total</TableHeader>
        </tr>
      </thead>
      <TableBody>
        {lines.map((line) => {
          const open = !expandable || expandedMenuId === line.menuId;

          return (
            <Fragment key={line.menuId}>
              <TableRow>
                <TableCell>
                  {expandable ? (
                    <button
                      type="button"
                      className="flex items-center gap-1.5 font-medium text-slate-900 hover:text-primary-700"
                      aria-expanded={open}
                      onClick={() =>
                        setExpandedMenuId((current) =>
                          current === line.menuId ? null : line.menuId,
                        )
                      }
                    >
                      {open ? (
                        <ChevronDown aria-hidden="true" className="size-4 shrink-0" />
                      ) : (
                        <ChevronRight aria-hidden="true" className="size-4 shrink-0" />
                      )}
                      {line.menuName}
                    </button>
                  ) : (
                    <span className="font-medium text-slate-900">{line.menuName}</span>
                  )}
                </TableCell>
                <TableCell>{resolveCatalogName(serviceTypeNames, line.serviceType)}</TableCell>
                <TableCell>
                  {line.dietaryTags.length === 0 ? (
                    '—'
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {line.dietaryTags.map((tag) => (
                        <Badge
                          key={tag}
                          variant={CRITICAL_DIETARY_TAGS.has(tag) ? 'warning' : 'neutral'}
                        >
                          {resolveCatalogName(dietaryTagNames, tag)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-semibold tabular-nums">{line.totalQuantity}</TableCell>
              </TableRow>

              {open ? (
                <TableRow>
                  <TableCell colSpan={4} className="bg-slate-50/80 p-0">
                    <div className="px-6 py-3">
                      <table className="w-full table-fixed text-left text-sm">
                        <caption className="sr-only">Desglose por empresa y sede</caption>
                        <thead>
                          <tr className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            <th scope="col" className="w-2/5 py-1 pr-4">
                              Empresa
                            </th>
                            <th scope="col" className="w-2/5 py-1 pr-4">
                              Sede
                            </th>
                            <th scope="col" className="w-1/5 py-1">
                              Cantidad
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {line.byCustomerSite.map((breakdown) => (
                            <tr key={`${breakdown.customerId}:${breakdown.siteId}`}>
                              <td className="truncate py-1 pr-4 text-slate-700">
                                {breakdown.customerName}
                              </td>
                              <td className="truncate py-1 pr-4 text-slate-700">
                                {breakdown.siteName}
                              </td>
                              <td className="py-1 font-medium tabular-nums text-slate-900">
                                {breakdown.quantity}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </TableCell>
                </TableRow>
              ) : null}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
